import { useState, useEffect } from 'react';
import { usePolicyStore, HrPolicy } from '../../../store/policyStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Plus, Search, BookOpen, FileText, CheckCircle, Pencil, Trash2 } from 'lucide-react';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/Select';
import toast from 'react-hot-toast';

const CATEGORIES = ['general', 'leave', 'conduct', 'benefits', 'security', 'it', 'finance', 'other'];
const STATUSES = ['draft', 'published'];

const todayISODate = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = {
    title: '', category: 'general', version: '1.0',
    effectiveDate: todayISODate(), status: 'draft', content: '',
};

export function HrPolicies() {
    const { policies, isLoading, fetchPolicies, createPolicy, updatePolicy, deletePolicy } = usePolicyStore();
    const [search, setSearch] = useState('');

    const [isFormOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchPolicies();
    }, []);

    const filteredPolicies = policies.filter(p =>
        p.title.toLowerCase().includes(search.toLowerCase()) ||
        p.category.toLowerCase().includes(search.toLowerCase())
    );

    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setErrors({});
        setFormOpen(true);
    };

    const openEdit = (p: HrPolicy) => {
        setEditingId(p.id);
        setForm({
            title: p.title ?? '',
            category: p.category ?? 'general',
            version: p.version ?? '1.0',
            effectiveDate: p.effectiveDate ? String(p.effectiveDate).slice(0, 10) : todayISODate(),
            status: p.status ?? 'draft',
            content: p.content ?? '',
        });
        setErrors({});
        setFormOpen(true);
    };

    const validate = () => {
        const next: Record<string, string> = {};
        if (!form.title.trim()) next.title = 'Title is required';
        if (!form.version.trim()) next.version = 'Version is required';
        if (!form.effectiveDate) next.effectiveDate = 'Effective date is required';
        if (!form.content.trim()) next.content = 'Policy content is required';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = {
                title: form.title.trim(),
                category: form.category,
                version: form.version.trim(),
                effectiveDate: form.effectiveDate,
                status: form.status,
                content: form.content.trim(),
            };
            if (editingId) {
                await updatePolicy(editingId, payload);
                toast.success('Policy updated');
            } else {
                await createPolicy(payload);
                toast.success('Policy created');
            }
            setFormOpen(false);
            fetchPolicies();
        } catch (e) {
            toast.error(editingId ? 'Failed to update policy' : 'Failed to create policy');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deletePolicy(deleteTarget.id);
            toast.success('Policy deleted');
            setDeleteTarget(null);
        } catch (e) {
            toast.error('Failed to delete policy');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">HR Policies</h1>
                    <p className="text-muted-foreground">Central repository for company handbooks and guidelines.</p>
                </div>
                <Button variant="primary" onClick={openCreate}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Policy
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                            <BookOpen className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Published Policies</p>
                            <h3 className="text-foreground font-display text-metric">
                                {policies.filter(p => p.status === 'published').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
                            <FileText className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Drafts</p>
                            <h3 className="text-foreground font-display text-metric">
                                {policies.filter(p => p.status === 'draft').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
                            <CheckCircle className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Recently Updated</p>
                            <h3 className="text-foreground font-display text-metric">
                                {policies.filter(p => new Date(p.updatedAt).getTime() > Date.now() - 30 * 24 * 60 * 60 * 1000).length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <h3 className="text-foreground font-display text-section-title">Policy Directory</h3>
                        <div className="relative w-64">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search policies..."
                                className="pl-9"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : (
                        <table className="w-full text-sm text-left">
                            <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                                <tr>
                                    <th className="px-6 py-3">Policy Title</th>
                                    <th className="px-6 py-3">Category</th>
                                    <th className="px-6 py-3">Version</th>
                                    <th className="px-6 py-3">Effective Date</th>
                                    <th className="px-6 py-3">Status</th>
                                    <th className="px-6 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredPolicies.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                                            No policies found.
                                        </td>
                                    </tr>
                                ) : filteredPolicies.map(p => (
                                    <tr key={p.id} className="hover:bg-secondary/20 transition-colors">
                                        <td className="px-6 py-4 font-medium text-foreground">
                                            {p.title}
                                        </td>
                                        <td className="px-6 py-4 capitalize">{p.category}</td>
                                        <td className="px-6 py-4">v{p.version}</td>
                                        <td className="px-6 py-4">
                                            {p.effectiveDate ? new Date(p.effectiveDate).toLocaleDateString() : '—'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant={p.status === 'published' ? 'success' : 'secondary'}>
                                                {p.status.toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon-sm"
                                                    onClick={() => openEdit(p)}
                                                    className="text-muted-foreground hover:text-foreground"
                                                    aria-label={`Edit ${p.title}`}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon-sm"
                                                    onClick={() => setDeleteTarget({ id: p.id, title: p.title })}
                                                    className="text-muted-foreground hover:text-destructive"
                                                    aria-label={`Delete ${p.title}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            {/* Create / Edit modal */}
            <Dialog open={isFormOpen} onOpenChange={setFormOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingId ? 'Edit Policy' : 'New Policy'}</DialogTitle>
                        <DialogDescription>
                            {editingId ? 'Update the policy details below.' : 'Create a company policy document.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Title</label>
                            <Input
                                placeholder="e.g. Code of Conduct"
                                value={form.title}
                                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                                invalid={!!errors.title}
                            />
                            {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Category</label>
                                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CATEGORIES.map((c) => (
                                            <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Version</label>
                                <Input
                                    placeholder="1.0"
                                    value={form.version}
                                    onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))}
                                    invalid={!!errors.version}
                                />
                                {errors.version && <p className="text-xs text-destructive">{errors.version}</p>}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Effective Date</label>
                                <Input
                                    type="date"
                                    value={form.effectiveDate}
                                    onChange={(e) => setForm((f) => ({ ...f, effectiveDate: e.target.value }))}
                                    invalid={!!errors.effectiveDate}
                                />
                                {errors.effectiveDate && <p className="text-xs text-destructive">{errors.effectiveDate}</p>}
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Status</label>
                                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STATUSES.map((s) => (
                                            <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Content</label>
                            <Textarea
                                rows={6}
                                placeholder="Write the policy content…"
                                value={form.content}
                                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                                invalid={!!errors.content}
                            />
                            {errors.content && <p className="text-xs text-destructive">{errors.content}</p>}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSubmit} isLoading={saving}>
                            {editingId ? 'Save Changes' : 'Create Policy'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <DialogContent className="max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>Delete policy</DialogTitle>
                        <DialogDescription>
                            Delete <span className="font-medium text-foreground">{deleteTarget?.title}</span>? This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete} isLoading={deleting}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
